import { useEffect, useRef, useState } from "react";
import { QueryClient, QueryCache } from 'react-query'
import { format } from "date-fns";

export function filterTransactions(transactions) {
    const { investment, liquidation } = transactions?.reduce((acc, trx) => {
        if (trx.type === "liquidation") acc.liquidation.push(trx)
        else acc.investment.push(trx)
        return acc
    }, { investment: [], liquidation: [] })

    return { investment, liquidation }
}

/**
 * Returns transaction authorization tokens for tokenized customer cards debited previously.
 * This function only works for a single customer's transactions.
 * @param {Array} transactions 
 * @param {String} email 
*/
export function getAuthorizationTokens(transactions, email) {
    const hash = {}
    const tokens = transactions?.reduce((acc, trx) => {
        if (
            trx.status === "success" && trx.channel === "card" &&
            trx.customer?.email === email && trx.authorization?.reusable
        ) {
            const key = `${trx.authorization.authorization_code} ${trx.authorization.signature}`
            if (!hash[key]) acc.push(trx.authorization)
            hash[key] = true
        }

        return acc
    }, [])

    return tokens
}

export function getTransactionInterest(trx, interestRate = 17.5) {
    /**
     * https://www.investopedia.com/terms/p/pro-rata.asp
     * Pro Rata for Interest Rates
        Pro rata calculations are also used to determine the amount of interest 
        that will be earned on an investment. If an investment earns an annual interest rate, 
        then the pro rata amount earned for a shorter period is calculated by 
        dividing the total amount of interest by the number of months in a year and 
        multiplying by the number of months in the truncated period. 
        The amount of interest earned in two months on an investment that 
        yields 10% interest each year is (10% / 12) x 2 = 1.67%.
    */

    const isSuccess = trx.status === "success" && trx.type !== "liquidation"
    const { amount, paidAt, createdAt } = trx
    const trxDate = paidAt ?? createdAt

    if (isSuccess) {
        //Rounding down means interest is on a full monthly basis (like 16 instead of 16.8)
        const elapsedMonths = getTimeDifference(trxDate) / 30
        //return new percentage value by elapsed months in the year
        const newRate = (interestRate / 12) * elapsedMonths
        //Dividing by 100 returns the decimal of the rate
        const interest = (newRate / 100) * amount
        return {
            amount,
            interest,
            rate: newRate.toFixed(1) + "%",
            elapsedMonths
        }
    }
    //Failed transactions
    return { amount, interest: 0, rate: 0 }
}

export function getTimeDifference(startDate) {
    if (startDate) {
        const now = new Date()
        const then = new Date(startDate)
        //Sat May 23 2020 23:51:52 GMT+0100 (West Africa Standard Time)

        const oneDay = 1000 * 60 * 60 * 24
        const elapsedMilliseconds = now.getTime() - then.getTime()
        const elapsedDays = Math.round(elapsedMilliseconds / oneDay)
        return elapsedDays
    }

    return 0
}

export function metricPrefix(number) {
    const num = Math.abs(number)
    //Must start from largest number so it doesn't return lower true condition
    if (num > 999999999) return Math.sign(num) * ((num / 1000000000).toFixed(2)) + 'B'
    if (num > 999999) return Math.sign(num) * ((num / 1000000).toFixed(2)) + 'M'
    if (num > 999) return Math.sign(num) * ((num / 1000).toFixed(2)) + 'k'
    if (num < 999) return Math.sign(num) * num
}

export function formatDate(date, long = true, time = false) {
    //so every function uses the same date format to avoid differences
    if (!date) return null
    const d = new Date(date)
    return format(d, `dd MMM ${long ? "yyyy" : "yy"} ${time ? "- hh:mm a" : ""}`)
}

export function formatKobo(num, currency = "NGN") {
    if (!num) return 0
    return `${currency}${(num / 100).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`
}

export function formatNumber(num, currency = "NGN") {
    return `${currency ?? ""}${num.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`
}

export const queryClientConfig = new QueryClient({
    queryCache: new QueryCache({
        onError: error => console.log(error),
    }),
    defaultOptions: {
        queries: {
            throwOnError: false,
            useErrorBoundary: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
        }
    }
})


export function getTimeOfDay() {
    const time = new Date().getHours()

    if (time >= 16) return "evening"
    else if (time >= 12 && time < 16) return "afternoon"
    else return "morning"
}

export function useRefSize(ref) {
    const [size, setSize] = useState({ width: undefined, height: undefined })

    useEffect(() => {
        const compStyles = window.getComputedStyle(ref.current);
        const width = Number(compStyles.getPropertyValue('width').replace(/[^\d.-]/g, ''))
        const height = Number(compStyles.getPropertyValue('height').replace(/[^\d.-]/g, ''))
        setSize({ width, height })
        //eslint-disable-next-line
    }, [ref]);

    return size
}

export function useLoadingMessage() {
    const messages = [
        `Approaching the Speedforce...`,
        // `Reticulating splines...`,
        // `Generating your dashboard...`,
        `Swapping time and space...`,
        `Spinning on the y-axis...`,
        `Tokenizing real life...`,
        `Summoning the internet...`,
        `Grabbing extra minions`,
        `Bending the spoon...`,
        `Counting forward from Infinity`,
        `Creating a time-loop inversion field`,
        `Spinning the wheels of fortune...`,
        `Counting backwards from Infinity`,
        `To the galaxies and beyond..`,
        `Solving calculus...`,
    ]
    const index = useRef(Math.random())
    const mult = num => Math.floor(num * messages.length)

    return messages[mult(index.current)]
}